import Gender from "constants/type/user/Gender";

/**
 * "+91 XXXXX X3210" — the one masking rule, util/maskPhone (product,
 * 2026-09-17: the LAST four digits shown). This used to keep the FIRST four
 * ("+91-9413XXXXXX"), a rule read off a design screenshot and never confirmed.
 *
 * Masking is entirely client-side: nothing in the estate returns a masked
 * patient phone.
 */
import { maskPhone } from "util/maskPhone";

export { maskPhone };

/**
 * The caller-number caption on the call confirmation dialog and the transcript
 * modal ("Calling from …") — the same one rule as every other number on screen,
 * "+91 XXXXX X3285" (product, 2026-09-18). It kept only the last TWO digits
 * ("+91 XX-XX-XX-XX85") and was missed when maskPhone became the rule.
 */
export const formatCallingFrom = (countryCode, phoneno) => maskPhone(countryCode, phoneno);

/**
 * "vinxxxxxxxxxx@gmail.com" — keep the first 3 of the local part and the domain.
 *
 * Same caveat as maskPhone: read off the screenshot, unconfirmed. Lowercase x
 * here because that is what the design shows, where the phone uses uppercase X.
 */
export const maskEmail = (email) => {
  const value = String(email || "").trim();
  const at = value.indexOf("@");
  if (at <= 0) return value;

  const local = value.slice(0, at);
  const domain = value.slice(at);
  if (local.length <= 3) return `${local}${domain}`;

  return `${local.slice(0, 3)}${"x".repeat(local.length - 3)}${domain}`;
};

/** "Purbita Banerjee" -> "Purbita B." for the top bar. */
export const abbreviateName = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "";
  if (parts.length === 1) return parts[0];

  return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.`;
};

/** "Female · 52 yr". The header spells gender out where the table abbreviates it. */
/**
 * "Male · 7 mo", "Female · 52 yr".
 *
 * `age` is either a LABEL already built by formatAgeLabel — which is what a
 * screen holding a dateOfBirth passes — or a bare number of years, which is all
 * an appointment ROW carries: the list endpoint sends `patientAge` and no date,
 * so months cannot be derived there.
 */
export const formatGenderAge = (gender, age) => {
  const g =
    gender === Gender.FEMALE ? "Female" : gender === Gender.MALE ? "Male" : "";
  const a = typeof age === "number" ? (age ? `${age} yr` : "") : String(age || "");

  return [g, a].filter(Boolean).join(" · ");
};

/**
 * dateOfBirth -> Date, or null when there is nothing usable.
 *
 * TWO wire forms, because the reads and the writes disagree: /patients/{id}
 * serialises epoch MILLIS, while the profile-update payload is documented as
 * `dateOfBirth:"YYYY-MM-DD"` (APIConfig.js:130). Number("1990-04-02") is NaN,
 * so a date STRING used to fall straight through to the stored `age` column —
 * silently, and that column is the stale one this function exists to avoid.
 */
const dobToDate = (dateOfBirth) => {
  if (dateOfBirth === null || dateOfBirth === undefined || dateOfBirth === "") return null;

  if (typeof dateOfBirth === "number") {
    // 0 stays "unset", not 1 Jan 1970 — it is the backend's empty sentinel.
    return dateOfBirth ? new Date(dateOfBirth) : null;
  }

  const s = String(dateOfBirth).trim();
  if (/^-?\d+$/.test(s)) {
    const ms = Number(s);
    return ms ? new Date(ms) : null;
  }

  /* "YYYY-MM-DD", and the date half of a full ISO stamp. Built as UTC on
     purpose: the arithmetic below is UTC throughout, so parsing the birthday
     locally would shift it by the timezone offset and cost a whole year for
     anyone born on the 1st when the browser sits west of UTC. */
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return null;
};

/**
 * Age from dateOfBirth, falling back to the stored `age`.
 *
 * The two are independent DB columns with no derivation between them, so they
 * can disagree and the stored one goes stale — which is the whole reason the
 * date is preferred over the number.
 */
export const resolveAge = (dateOfBirth, age) => {
  const dob = dobToDate(dateOfBirth);
  if (!dob || isNaN(dob.getTime())) return age || 0;

  const now = new Date();
  let years = now.getUTCFullYear() - dob.getUTCFullYear();
  const m = now.getUTCMonth() - dob.getUTCMonth();
  if (m < 0 || (m === 0 && now.getUTCDate() < dob.getUTCDate())) years -= 1;

  return years >= 0 && years < 130 ? years : age || 0;
};

/**
 * The age a screen SHOWS: MONTHS under a year, whole years above it (Nittin,
 * 2026-09-24). "1 yr" for a seven-month-old is wrong in the one band where
 * months are the unit that matters, and rounding down to "0 yr" reads as
 * unknown.
 *
 * Months need the DATE. The stored `age` column is a whole number of years and
 * cannot answer this, so a patient with no usable dateOfBirth still reads in
 * years — the same fallback resolveAge makes, for the same reason.
 *
 * Returns "" for an age nobody knows, so callers can drop the whole line rather
 * than print "0".
 */
export const formatAgeLabel = (dateOfBirth, age) => {
  const years = resolveAge(dateOfBirth, age);
  if (years === 0) {
    const dob = dobToDate(dateOfBirth);
    if (dob && !isNaN(dob.getTime())) {
      const now = new Date();
      let months =
        (now.getUTCFullYear() - dob.getUTCFullYear()) * 12 +
        (now.getUTCMonth() - dob.getUTCMonth());
      /* The same day-of-month rule resolveAge uses, so the two never disagree
         about where the year boundary is. */
      if (now.getUTCDate() < dob.getUTCDate()) months -= 1;
      /* A future date is not an age. resolveAge rejects one already; this is
         the case where the stored age is 0 too, so there is nothing to show. */
      if (months >= 0) return `${months} mo`;
    }
  }
  return years ? `${years} yr` : "";
};

/**
 * WHOLE MONTHS, but only under a year — 0 above it, and 0 when there is no
 * usable date.
 *
 * Split out from formatAgeLabel because two callers need the NUMBER rather than
 * the words: Create Rx puts it on the wire (`ageMonths`, an int beside the int
 * `age` of whole years), and its card prints the number and the unit in
 * separate boxes. A caller that wants the words should use formatAgeLabel —
 * they share this arithmetic rather than each doing their own.
 */
export const ageInMonths = (dateOfBirth) => {
  const dob = dobToDate(dateOfBirth);
  if (!dob || isNaN(dob.getTime())) return 0;

  const now = new Date();
  let months =
    (now.getUTCFullYear() - dob.getUTCFullYear()) * 12 +
    (now.getUTCMonth() - dob.getUTCMonth());
  /* The same day-of-month rule resolveAge uses, so the two never disagree about
     where the year boundary is. */
  if (now.getUTCDate() < dob.getUTCDate()) months -= 1;

  return months >= 0 && months < 12 ? months : 0;
};

/**
 * "Hindi, English, +8 more".
 *
 * `value` from /ppl is a copy of `code` — there is no display label, so whatever
 * the server stores is what shows. If codes turn out to be ISO ("hi", "en")
 * this reads honestly but ugly, and a code->name map would be needed.
 */
/**
 * The /ppl response, as an array — whatever envelope it arrives in.
 *
 * dev1 answers a BARE ARRAY of `{patientId, code, value}`. Every wrapper name
 * this endpoint family uses elsewhere is accepted too, because an unrecognised
 * one used to fall through to `[]` and render as "no languages recorded" — a
 * clinical claim rather than a parse failure.
 *
 * Lives here rather than in PatientDetails because TWO screens need it now: the
 * patient header and Create Rx, which shares that header and used to hand it a
 * hardcoded empty list.
 */
export const normalizeLanguages = (res) => {
  if (Array.isArray(res)) return res;
  if (!res || typeof res !== "object") return [];
  const wrapped =
    res.languages || res.preferredLanguages || res.data || res.items || res.result;
  return Array.isArray(wrapped) ? wrapped : [];
};

export const formatLanguages = (languages, visible = 2) => {
  const names = (languages || [])
    .map((l) => (l && (l.value || l.code)) || "")
    .filter(Boolean);
  if (!names.length) return "";

  const shown = names.slice(0, visible).join(", ");
  const rest = names.length - visible;

  return rest > 0 ? `${shown}, +${rest} more` : shown;
};

/** "20 Feb 2026" — UTC, so the date can't slip a day in another timezone. */
const longDate = new Intl.DateTimeFormat("en-GB", {
  timeZone: "UTC",
  day: "numeric",
  month: "short",
  year: "numeric",
});

export const formatLongDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);

  return isNaN(d.getTime()) ? "" : longDate.format(d);
};

/**
 * "14 Mar 1974" from a dateOfBirth, in either of its two wire forms.
 *
 * NOT formatLongDate: that one hands its argument straight to `new Date`, which
 * reads epoch-millis-as-a-STRING as an invalid date, and reads the 0 sentinel as
 * 1 Jan 1970 rather than "unset". dobToDate already knows both, so use it.
 */
export const formatDob = (dateOfBirth) => {
  const d = dobToDate(dateOfBirth);

  return d && !isNaN(d.getTime()) ? longDate.format(d) : "";
};
